import { useState, useCallback, useRef, useEffect } from 'react';
import type { InlineEditState, InlineEditVariant } from './types';

interface UseInlineEditOptions {
  value: string | number | boolean | null;
  onSave: (newValue: string | number | boolean) => Promise<void>;
  variant: InlineEditVariant;
  validate?: (value: string | number | boolean) => string | null;
  required?: boolean;
}

const IMMEDIATE_VARIANTS: InlineEditVariant[] = ['select', 'color', 'switch'];

export function useInlineEdit({ value, onSave, variant, validate, required }: UseInlineEditOptions) {
  const [state, setState] = useState<InlineEditState>('display');
  const [editValue, setEditValue] = useState<string | number | boolean>(value ?? '');
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);
  const isSavingRef = useRef(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isImmediate = IMMEDIATE_VARIANTS.includes(variant);

  // Sync editValue when external value changes while in display mode
  useEffect(() => {
    if (state === 'display') {
      setEditValue(value ?? '');
    }
  }, [value, state]);

  // Cleanup success timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const startEditing = useCallback(() => {
    if (state !== 'display') return;
    setEditValue(value ?? '');
    setError(null);
    setState('editing');
    // Auto-focus on next tick after render
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [state, value]);

  const cancel = useCallback(() => {
    setEditValue(value ?? '');
    setError(null);
    setState('display');
  }, [value]);

  const save = useCallback(
    async (valueToSave?: string | number | boolean) => {
      if (isSavingRef.current) return;

      const finalValue = valueToSave !== undefined ? valueToSave : editValue;

      // Required validation
      if (required && (finalValue === '' || finalValue === null || finalValue === undefined)) {
        setError('required');
        return;
      }

      // Custom validation
      if (validate) {
        const validationError = validate(finalValue);
        if (validationError) {
          setError(validationError);
          return;
        }
      }

      // Skip save if value hasn't changed
      if (finalValue === (value ?? '')) {
        setState('display');
        setError(null);
        return;
      }

      isSavingRef.current = true;
      setState('saving');
      setError(null);

      try {
        await onSave(finalValue);
        setState('display');
        setShowSuccess(true);

        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        successTimeoutRef.current = setTimeout(() => {
          setShowSuccess(false);
        }, 1500);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'save_failed';
        setError(message);
        setState('editing');
      } finally {
        isSavingRef.current = false;
      }
    },
    [editValue, value, onSave, validate, required],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
        return;
      }

      if (variant === 'textarea') {
        // Ctrl+Enter or Cmd+Enter to save for textarea
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          save();
        }
      } else {
        // Enter to save for all other text-like variants
        if (e.key === 'Enter') {
          e.preventDefault();
          save();
        }
      }
    },
    [variant, save, cancel],
  );

  const handleBlur = useCallback(() => {
    if (state === 'editing' && !isSavingRef.current) {
      save();
    }
  }, [state, save]);

  const handleImmediateChange = useCallback(
    (newValue: string | number | boolean) => {
      setEditValue(newValue);
      save(newValue);
    },
    [save],
  );

  return {
    state,
    editValue,
    setEditValue,
    error,
    showSuccess,
    inputRef,
    isImmediate,
    startEditing,
    cancel,
    save,
    handleKeyDown,
    handleBlur,
    handleImmediateChange,
  };
}
